import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import moment from 'moment';

const PatrolCard = ({
  patrol,
  patrolNumber,
  isFirst,
  isLast,
  onPatrolClick,
  onMoveUp,
  onMoveDown,
}) => {
  const handleCardClick = () => {
    onPatrolClick?.(patrol);
  };

  return (
    <Card
      elevation={2}
      onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 4,
          backgroundColor: 'action.hover',
        },
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header Row - Company Name and Reorder Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ color: 'primary.main', marginRight: 1, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {patrol.CustomerName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Reorder Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
              <Tooltip title="Move up" placement="left">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp?.();
                    }}
                    disabled={isFirst}
                    sx={{
                      padding: '2px',
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down" placement="left">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown?.();
                    }}
                    disabled={isLast}
                    sx={{
                      padding: '2px',
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Location */}
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 1.5 }}>
          {patrol.AddressName}
        </Typography>

        {/* Guard Name */}
        <Box sx={{ mb: 1 }}>
          <Chip
            icon={<PersonIcon />}
            label={patrol.ContactName}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Date and Time Info */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CalendarTodayIcon />}
            label={moment(patrol.StartTime).format('ddd, MMM D')}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500 }}
          />
          <Chip
            icon={<AccessTimeIcon />}
            label={`${moment(patrol.StartTime).format('h:mm A')} - ${moment(
              patrol.EndTime
            ).format('h:mm A')}`}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Checklists */}
        {patrol.Checklists && patrol.Checklists.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 0.75,
              }}
            >
              <ChecklistIcon
                sx={{ fontSize: 14, color: 'text.secondary' }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                Checklists
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {patrol.Checklists.map((checklist) => (
                <Chip
                  key={checklist.ChecklistID}
                  label={checklist.Name}
                  size="small"
                  color={
                    checklist.CustomerID && checklist.AddressID
                      ? 'primary'
                      : 'default'
                  }
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

PatrolCard.propTypes = {
  patrol: PropTypes.shape({
    ScheduleAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    CustomerName: PropTypes.string,
    ContactName: PropTypes.string,
    AddressName: PropTypes.string,
    StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    Checklists: PropTypes.arrayOf(
      PropTypes.shape({
        ChecklistID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Name: PropTypes.string,
        CustomerID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        AddressID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }).isRequired,
  patrolNumber: PropTypes.number.isRequired,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  onPatrolClick: PropTypes.func,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
};

export default memo(PatrolCard);
